import React, { useCallback, useMemo, CSSProperties, useEffect } from "react";
import { Spin, Checkbox, message, Input, Button, Tabs, Select, Modal, Tooltip } from "antd";
import { isValidMobile, isValidEmail, LoginFormContext, LoginFormContextType } from "../../utils";
import ResendCode from "../../ResendCode";
import GlobalContext, { GlobalContextConfig } from "@/context/GlobalContext";
import { useTranslation } from "react-i18next";
import NetworkSettingsModal from "@/components/Modals/NetworkSettingsModal";
import { BackNav } from "../../comps";
import { IcVipLogin, IcNormalLogin } from "@/assets/svg";
import { TeamLoginTypeInfo, TeamLoginTypeRes, AuthDataRes } from "@/types/LoginPage";
import isUndefined from "lodash/isUndefined";
import { changeEgovUrl, crossAppSwitch } from "@/utils/tools";
const { TabPane } = Tabs;
const { Option } = Select;
import QrcodeForm from "./QrcodeForm";
import MobileInputForm from "./MobileInputForm";
import styles from '../../index.module.less'
import styles1 from "./index.module.less";

const STATUS = {
  WAIT_SCAN: "WAIT_SCAN", // 等待扫描
  WAIT_CONFIRM: "WAIT_CONFIRM", // 扫描完成后  等待手机端确认
  EXPIRED: "EXPIRED", // 二维码已过期
  SUCCESS: "SUCCESS", // 验证成功
  WAIT_REDIRECT: "WAIT_REDIRECT", // 确认登录，等待跳转
  REDIRECT: "REDIRECT", // 跳转
  HAS_SCANNED: "HAS_SCANNED", //
  FETCHING: "FETCHING", // 加载中
  CANCEL: "CANCEL",
  TICKET_LOGIN: "TICKET_LOGIN" // 公共账号、个人账号选择登录
};

// eslint-disable-next-line complexity
export default function LoginForm({
  onSelAccount
}: {
  onSelAccount: (authData: AuthDataRes, tag: string) => void;
}) {
  const { t } = useTranslation("common");
  const {
    onLoginTypeChange,
    onTeamIdChange,
    onMobileChange,
    onEmailChange,
    onLoginModeChange,
    autoLogin,
    onAutoLoginChange,
    loginType,
    loginMode,
    navigatePath
  } = React.useContext<LoginFormContextType>(LoginFormContext);
  const { onGlobalError, appRuntimeEnv, service, focusSDK } =
    React.useContext<GlobalContextConfig>(GlobalContext);

  const onlyMobileLogin = useMemo(() => {
    const onlyMobileLoginConfig = appRuntimeEnv.getUIConfig("onlyMobileLogin");
    return !!onlyMobileLoginConfig;
  }, [appRuntimeEnv]);

  const canVipLogin = useMemo(() => {
    const canLogin = appRuntimeEnv.getUIConfig("canVipLogin");
    if (!isUndefined(canLogin) && !canLogin) {
      return false;
    }
    if (onlyMobileLogin) {
      return false;
    }
    return true;
  }, [appRuntimeEnv, onlyMobileLogin]);

  const [loading, setLoading] = React.useState(false);
  const [enterpriseDomain, setEnterpriseDomain] = React.useState("");
  const [teamLoginTypeList, setTeamLoginTypeList] = React.useState([] as TeamLoginTypeInfo[]);
  const [qrcodeStatus, setQrcodeStatus] = React.useState(STATUS.WAIT_SCAN);
  // const [scanCode, setScanCode] = React.useState(true);

  const onlineCallback = async () => {
    focusSDK.printLog("二维码-online");
    // clearTimer();
    // getCode();
  };
  const offlineCallback = () => {
    focusSDK.printLog("二维码-offlineCallback");
    // clearTimer();
    setQrcodeStatus(STATUS.EXPIRED);
  };

  useEffect(() => {
    window.addEventListener("online", onlineCallback);
    window.addEventListener("offline", offlineCallback);
    return () => {
      window.removeEventListener("online", onlineCallback);
      window.removeEventListener("offline", offlineCallback);
      // clearTimer();
    };
    // eslint-disable-next-line
  }, []);

  React.useEffect(() => {
    setTeamLoginTypeList(teamLoginTypeList);
  }, [teamLoginTypeList]);

  const [networkSettingsModalVisible, setNetworkSettingsModalVisible] = React.useState(false);

  const getTeamLoginType = React.useCallback(async () => {
    let typeList: TeamLoginTypeInfo[] = [] as TeamLoginTypeInfo[];
    try {
      const res: TeamLoginTypeRes = await service.authService.getTeamLoginType({
        domainName: enterpriseDomain
      });
      if (res) {
        typeList = res.typeList;
        onTeamIdChange(res.teamId);
        onLoginTypeChange(typeList[0].type);
      }
      setTeamLoginTypeList(typeList);
      setEnterpriseDomain("");
      return typeList;
    } catch (err) {}
  }, [service.authService, enterpriseDomain, onTeamIdChange, onLoginTypeChange]);

  const checkAndGetLoginType = useCallback(async () => {
    if (!enterpriseDomain) {
      return;
    }
    setLoading(true);
    const typeList = (await getTeamLoginType()) || [];
    setLoading(false);
  }, [enterpriseDomain, setLoading, getTeamLoginType]);

  // const mobileInputSectionComp = useMemo(() => <div />, []);

  const loginTitleComp = (
    <div className={styles1['login_title']}>
      {appRuntimeEnv.getI18N(appRuntimeEnv.getUIConfig("login.title"), {
        productName: appRuntimeEnv.productName
      })}
      <small>{appRuntimeEnv.key === "beta" ? "beta" : ""}</small>
    </div>
  );

  return (
    <div className={styles.mobileForm}>
      {loginMode === "vip" ? (
        <>
          {!teamLoginTypeList.length ? (
            <BackNav
              clickHandle={() => {
                onLoginModeChange("");
                onLoginTypeChange("MOBILE");
              }}
              title={t("login.back")}
              subtitle={t("login.vipLogin")}
              desc={t("login.inputEnterpriseDomainLong")}
            />
          ) : (
            <h4 style={{ margin: 0, textAlign: "center" }}>
              {appRuntimeEnv.getI18N(appRuntimeEnv.getUIConfig("login.title"), {
                productName: appRuntimeEnv.productName
              })}
              <small>{appRuntimeEnv.key === "beta" ? "beta" : ""}</small>
            </h4>
          )}
          <MobileInputForm
            teamLoginTypeList={teamLoginTypeList}
            enterpriseDomain={enterpriseDomain}
            onlyMobileLogin={onlyMobileLogin}
            setEnterpriseDomain={setEnterpriseDomain}
            checkAndGetLoginType={checkAndGetLoginType}
            loading={loading}
            setLoading={setLoading}
            autoLogin={autoLogin}
            qrcodeStatus={qrcodeStatus}
            STATUS={STATUS}
            onAutoLoginChange={onAutoLoginChange}
          />
          <div className={styles.otherLogin}>
            <p>
              <span>{t("login.otherLoginMode")}</span>
            </p>
            <Button
              onClick={() => {
                onLoginModeChange("");
                setTeamLoginTypeList([]);
                onLoginTypeChange("MOBILE");
              }}
            >
              <IcNormalLogin />
              {t("login.normalLogin")}
            </Button>
          </div>
        </>
      ) : loginMode === "register" ? (
        <>
          <BackNav
            clickHandle={() => onLoginModeChange("")}
            title={t("login.back")}
            subtitle={t("login.newUserRegister")}
          />
          <MobileInputForm
            teamLoginTypeList={teamLoginTypeList}
            enterpriseDomain={enterpriseDomain}
            onlyMobileLogin={onlyMobileLogin}
            setEnterpriseDomain={setEnterpriseDomain}
            checkAndGetLoginType={checkAndGetLoginType}
            loading={loading}
            setLoading={setLoading}
            autoLogin={autoLogin}
            qrcodeStatus={qrcodeStatus}
            STATUS={STATUS}
            onAutoLoginChange={onAutoLoginChange}
          />
        </>
      ) : (
        <>
          {loginType !== "QRCODE" && loginTitleComp}
          {loginType === "QRCODE" ? (
            <QrcodeForm
              changeEgovUrl={changeEgovUrl}
              qrcodeStatus={qrcodeStatus}
              STATUS={STATUS}
              setQrcodeStatus={setQrcodeStatus}
              onSelAccount={onSelAccount}
              autoLogin={autoLogin}
              onAutoLoginChange={onAutoLoginChange}
              loginType={loginType}
            />
          ) : (
            <MobileInputForm
              teamLoginTypeList={teamLoginTypeList}
              enterpriseDomain={enterpriseDomain}
              onlyMobileLogin={onlyMobileLogin}
              setEnterpriseDomain={setEnterpriseDomain}
              checkAndGetLoginType={checkAndGetLoginType}
              loading={loading}
              setLoading={setLoading}
              autoLogin={autoLogin}
              qrcodeStatus={qrcodeStatus}
              STATUS={STATUS}
              onAutoLoginChange={onAutoLoginChange}
            />
          )}
          {canVipLogin && (
            <div className={styles.otherLogin}>
              <p>
                <span>{t("login.otherLoginMode")}</span>
              </p>
              <Button
                onClick={() => {
                  onLoginModeChange("vip");
                  onLoginTypeChange("DOMAIN");
                  onMobileChange("");
                  onEmailChange("");
                }}
              >
                <IcVipLogin />
                {t("login.vipLogin")}
              </Button>
            </div>
          )}
        </>
      )}
      <div className={styles.debugSetting}>
        {appRuntimeEnv.debugMode && (
          <Button
            style={{
              height: 30,
              backgroundColor: "#E4F1FE",
              borderRadius: "4px 0 0 4px",
              border: "none"
            }}
            block
            onClick={() => {
              setNetworkSettingsModalVisible(true);
            }}
          >
            环境设置
          </Button>
        )}
      </div>
      {navigatePath === "step2" && <ResendCode loginType={loginType} />}
      <NetworkSettingsModal
        visible={networkSettingsModalVisible}
        onCancel={() => {
          setNetworkSettingsModalVisible(false);
        }}
      />
    </div>
  );
}
